import React from 'react';
import styled from 'styled-components';

const ModalOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(10, 22, 40, 0.95);
  z-index: ${({ theme }) => theme.zIndex.modal};
  display: flex;
  align-items: center;
  justify-content: center;
  padding: ${({ theme }) => theme.spacing.base};
  backdrop-filter: blur(4px);
`;

const ModalContent = styled.div`
  background: ${({ theme }) => theme.colors.background.primary};
  border-radius: ${({ theme }) => theme.borderRadius.lg};
  box-shadow: ${({ theme }) => theme.shadows['2xl']};
  max-width: 800px;
  width: 100%;
  max-height: 90vh;
  overflow-y: auto;
  position: relative;
  border: 1px solid ${({ theme }) => theme.colors.border.light};
`;

const ModalHeader = styled.div`
  padding: ${({ theme }) => `${theme.spacing.xl} ${theme.spacing.xl} ${theme.spacing.lg}`};
  border-bottom: 1px solid ${({ theme }) => theme.colors.border.light};
  background: ${({ theme }) => theme.colors.background.secondary};
  border-radius: ${({ theme }) => `${theme.borderRadius.lg} ${theme.borderRadius.lg} 0 0`};
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const ModalTitle = styled.h2`
  font-family: ${({ theme }) => theme.typography.fontFamily.primary};
  font-size: ${({ theme }) => theme.typography.fontSize['2xl']};
  font-weight: ${({ theme }) => theme.typography.fontWeight.bold};
  color: ${({ theme }) => theme.colors.text.primary};
  margin: 0;
  letter-spacing: ${({ theme }) => theme.typography.letterSpacing.tight};

  @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
    font-size: ${({ theme }) => theme.typography.fontSize.xl};
  }
`;

const CloseButton = styled.button`
  background: none;
  border: none;
  font-size: ${({ theme }) => theme.typography.fontSize['2xl']};
  color: ${({ theme }) => theme.colors.text.muted};
  cursor: pointer;
  padding: ${({ theme }) => theme.spacing.xs};
  border-radius: ${({ theme }) => theme.borderRadius.base};
  transition: all ${({ theme }) => theme.transitions.base};
  display: flex;
  align-items: center;
  justify-content: center;
  width: 40px;
  height: 40px;

  &:hover {
    background: ${({ theme }) => theme.colors.background.muted};
    color: ${({ theme }) => theme.colors.text.primary};
  }

  &:focus {
    outline: 2px solid ${({ theme }) => theme.colors.primary};
    outline-offset: 2px;
  }
`;

const ModalBody = styled.div`
  padding: ${({ theme }) => theme.spacing.xl};
  line-height: ${({ theme }) => theme.typography.lineHeight.relaxed};

  @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
    padding: ${({ theme }) => theme.spacing.lg};
  }
`;

const Section = styled.section`
  margin-bottom: ${({ theme }) => theme.spacing.xl};

  &:last-child {
    margin-bottom: 0;
  }
`;

const SectionTitle = styled.h3`
  font-family: ${({ theme }) => theme.typography.fontFamily.primary};
  font-size: ${({ theme }) => theme.typography.fontSize.lg};
  font-weight: ${({ theme }) => theme.typography.fontWeight.semibold};
  color: ${({ theme }) => theme.colors.text.primary};
  margin: 0 0 ${({ theme }) => theme.spacing.base} 0;
  text-transform: uppercase;
  letter-spacing: ${({ theme }) => theme.typography.letterSpacing.wide};
`;

const Paragraph = styled.p`
  font-family: ${({ theme }) => theme.typography.fontFamily.primary};
  font-size: ${({ theme }) => theme.typography.fontSize.base};
  font-weight: ${({ theme }) => theme.typography.fontWeight.normal};
  color: ${({ theme }) => theme.colors.text.primary};
  margin: 0 0 ${({ theme }) => theme.spacing.base} 0;
  line-height: ${({ theme }) => theme.typography.lineHeight.relaxed};

  &:last-child {
    margin-bottom: 0;
  }
`;

const List = styled.ul`
  font-family: ${({ theme }) => theme.typography.fontFamily.primary};
  font-size: ${({ theme }) => theme.typography.fontSize.base};
  color: ${({ theme }) => theme.colors.text.primary};
  margin: 0 0 ${({ theme }) => theme.spacing.base} ${({ theme }) => theme.spacing.lg};
  line-height: ${({ theme }) => theme.typography.lineHeight.relaxed};
`;

const ListItem = styled.li`
  margin-bottom: ${({ theme }) => theme.spacing.xs};

  &:last-child {
    margin-bottom: 0;
  }
`;

const ContactInfo = styled.div`
  background: ${({ theme }) => theme.colors.background.secondary};
  padding: ${({ theme }) => theme.spacing.lg};
  border-radius: ${({ theme }) => theme.borderRadius.base};
  border-left: 4px solid ${({ theme }) => theme.colors.primary};
  margin: ${({ theme }) => theme.spacing.lg} 0;
`;

const ContactTitle = styled.h4`
  font-family: ${({ theme }) => theme.typography.fontFamily.primary};
  font-size: ${({ theme }) => theme.typography.fontSize.base};
  font-weight: ${({ theme }) => theme.typography.fontWeight.semibold};
  color: ${({ theme }) => theme.colors.text.primary};
  margin: 0 0 ${({ theme }) => theme.spacing.xs} 0;
`;

const ContactText = styled.p`
  font-family: ${({ theme }) => theme.typography.fontFamily.primary};
  font-size: ${({ theme }) => theme.typography.fontSize.sm};
  color: ${({ theme }) => theme.colors.text.secondary};
  margin: 0;
`;

const Disclaimer = styled.div`
  background: ${({ theme }) => theme.colors.background.muted};
  padding: ${({ theme }) => theme.spacing.lg};
  border-radius: ${({ theme }) => theme.borderRadius.base};
  border: 1px solid ${({ theme }) => theme.colors.border.light};
  margin: ${({ theme }) => theme.spacing.lg} 0;
`;

const TermsOfService = ({ isOpen, onClose }) => {
  const handleOverlayClick = (e) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  React.useEffect(() => {
    const handleKeyDownEffect = (e) => {
      if (e.key === 'Escape') {
        onClose();
      }
    };

    if (isOpen) {
      document.addEventListener('keydown', handleKeyDownEffect);
      document.body.style.overflow = 'hidden';
    }

    return () => {
      document.removeEventListener('keydown', handleKeyDownEffect);
      document.body.style.overflow = 'unset';
    };
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  return (
    <ModalOverlay onClick={handleOverlayClick}>
      <ModalContent>
        <ModalHeader>
          <ModalTitle>Terms of Service</ModalTitle>
          <CloseButton onClick={onClose} aria-label="Close Terms of Service">
            ×
          </CloseButton>
        </ModalHeader>
        
        <ModalBody>
          <Section>
            <Paragraph>
              <strong>Effective Date:</strong> July 22, 2025
            </Paragraph>
            <Paragraph>
              <strong>Last Updated:</strong> July 22, 2025
            </Paragraph>
          </Section>

          <Section>
            <SectionTitle>Agreement to Terms</SectionTitle>
            <Paragraph>
              These Terms of Service ("Terms") constitute a legally binding agreement between you and 
              Antony Haines Consulting ("Company," "we," "our," or "us") regarding your use of our 
              website antonyhaines.com and engagement with our fractional CTO consulting services.
            </Paragraph>
            <Paragraph>
              By accessing our website or engaging our services, you agree to be bound by these Terms. 
              If you disagree with any part of these Terms, you may not access our website or use our services.
            </Paragraph>
          </Section>

          <Section>
            <SectionTitle>Services Description</SectionTitle>
            <Paragraph>
              Antony Haines Consulting provides fractional Chief Technology Officer (CTO) services 
              specifically designed for US mid-market manufacturing companies with $100M-$2B revenue. 
              Our services include:
            </Paragraph>
            <List>
              <ListItem>Strategic technology planning and assessment</ListItem>
              <ListItem>IT/OT (Information Technology/Operational Technology) convergence consulting</ListItem>
              <ListItem>Technology crisis response and resolution</ListItem>
              <ListItem>Digital transformation strategy and implementation guidance</ListItem>
              <ListItem>Fractional CTO leadership services</ListItem>
              <ListItem>Technology risk assessment and mitigation</ListItem>
              <ListItem>Executive technology advisory services</ListItem>
            </List>
          </Section>

          <Section>
            <SectionTitle>Zoom Consultation and Engagement Process</SectionTitle>
            <Paragraph>
              Our engagement process typically follows these stages:
            </Paragraph>
            <List>
              <ListItem><strong>Free Zoom Consultation:</strong> Initial assessment of your technology challenges and strategic fit</ListItem>
              <ListItem><strong>Deep Assessment:</strong> Comprehensive evaluation of current technology landscape</ListItem>
              <ListItem><strong>Discovery Project:</strong> Focused analysis and strategic recommendation development</ListItem>
              <ListItem><strong>Ongoing Fractional CTO Services:</strong> Continuous strategic technology leadership</ListItem>
            </List>
            <Paragraph>
              All services are subject to mutual agreement on scope, timeline, and compensation 
              through a separate consulting agreement.
            </Paragraph>
          </Section>

          <Section>
            <SectionTitle>Client Responsibilities</SectionTitle>
            <Paragraph>
              To ensure successful consulting engagements, clients agree to:
            </Paragraph>
            <List>
              <ListItem>Provide accurate and complete information about their technology environment</ListItem>
              <ListItem>Grant reasonable access to systems, personnel, and documentation as needed</ListItem>
              <ListItem>Maintain confidentiality of proprietary consulting methodologies and tools</ListItem>
              <ListItem>Pay fees according to agreed-upon terms and schedules</ListItem>
              <ListItem>Communicate openly about challenges, constraints, and strategic objectives</ListItem>
              <ListItem>Implement recommendations within their organizational capabilities and risk tolerance</ListItem>
            </List>
          </Section>

          <Section>
            <SectionTitle>Intellectual Property Rights</SectionTitle>
            <Paragraph>
              <strong>Company Intellectual Property:</strong> All proprietary methodologies, frameworks, 
              templates, and consulting tools remain the exclusive property of Antony Haines Consulting. 
              This includes our AI-assisted intelligence systems and strategic assessment frameworks.
            </Paragraph>
            <Paragraph>
              <strong>Client Intellectual Property:</strong> All client data, business processes, and 
              proprietary information remain the exclusive property of the client. We do not claim 
              ownership of client intellectual property.
            </Paragraph>
            <Paragraph>
              <strong>Work Product:</strong> Reports, recommendations, and strategic plans developed 
              during engagements become the property of the client, while underlying methodologies 
              remain our intellectual property.
            </Paragraph>
          </Section>

          <Section>
            <SectionTitle>Confidentiality and Non-Disclosure</SectionTitle>
            <Paragraph>
              We maintain strict confidentiality regarding all client information and business operations. 
              This includes:
            </Paragraph>
            <List>
              <ListItem>Technical specifications and system architectures</ListItem>
              <ListItem>Business processes and operational procedures</ListItem>
              <ListItem>Financial information and performance metrics</ListItem>
              <ListItem>Strategic plans and competitive information</ListItem>
              <ListItem>Personnel information and organizational structures</ListItem>
            </List>
            <Paragraph>
              Confidentiality obligations survive the termination of any consulting engagement and 
              are subject to additional protections in our consulting agreements.
            </Paragraph>
          </Section>

          <Section>
            <SectionTitle>Limitations of Liability</SectionTitle>
            <Paragraph>
              Our consulting services are advisory in nature. While we strive to provide accurate 
              and beneficial recommendations:
            </Paragraph>
            <List>
              <ListItem>Implementation decisions and results remain the responsibility of the client</ListItem>
              <ListItem>We do not guarantee specific business outcomes or financial results</ListItem>
              <ListItem>Our liability is limited to the fees paid for the specific engagement</ListItem>
              <ListItem>We are not liable for indirect, consequential, or punitive damages</ListItem>
              <ListItem>Force majeure events and circumstances beyond our control are excluded from liability</ListItem>
            </List>
            
            <Disclaimer>
              <ContactText>
                <strong>Important:</strong> Our recommendations are based on information provided 
                and industry best practices. Final implementation decisions must consider your 
                specific business context, risk tolerance, and regulatory requirements.
              </ContactText>
            </Disclaimer>
          </Section>

          <Section>
            <SectionTitle>Professional Standards</SectionTitle>
            <Paragraph>
              We adhere to the highest professional standards in all consulting engagements:
            </Paragraph>
            <List>
              <ListItem>Maintaining independence and objectivity in all recommendations</ListItem>
              <ListItem>Avoiding conflicts of interest and disclosing any potential conflicts</ListItem>
              <ListItem>Providing services only within our areas of expertise and competence</ListItem>
              <ListItem>Respecting client confidentiality and proprietary information</ListItem>
              <ListItem>Delivering services with professional care and diligence</ListItem>
              <ListItem>Communicating clearly about scope, limitations, and assumptions</ListItem>
            </List>
          </Section>

          <Section>
            <SectionTitle>Payment Terms</SectionTitle>
            <Paragraph>
              Payment terms are established in individual consulting agreements and typically include:
            </Paragraph>
            <List>
              <ListItem>Clear scope of work and deliverables</ListItem>
              <ListItem>Defined payment schedules and milestones</ListItem>
              <ListItem>Expense reimbursement policies</ListItem>
              <ListItem>Late payment fees and collection procedures</ListItem>
              <ListItem>Currency and payment method specifications</ListItem>
            </List>
          </Section>

          <Section>
            <SectionTitle>Termination</SectionTitle>
            <Paragraph>
              Either party may terminate consulting engagements according to the terms specified 
              in the individual consulting agreement. Termination procedures typically include:
            </Paragraph>
            <List>
              <ListItem>Written notice requirements and timelines</ListItem>
              <ListItem>Payment obligations for work completed</ListItem>
              <ListItem>Return of confidential materials and information</ListItem>
              <ListItem>Survival of confidentiality and intellectual property obligations</ListItem>
              <ListItem>Transition assistance and knowledge transfer procedures</ListItem>
            </List>
          </Section>

          <Section>
            <SectionTitle>Governing Law and Dispute Resolution</SectionTitle>
            <Paragraph>
              These Terms and any consulting agreements are governed by the laws of the United States 
              and the state where services are primarily performed. Any disputes will be resolved through:
            </Paragraph>
            <List>
              <ListItem>Good faith negotiation as the first step</ListItem>
              <ListItem>Mediation through a mutually agreed-upon mediator</ListItem>
              <ListItem>Binding arbitration if mediation is unsuccessful</ListItem>
              <ListItem>Appropriate jurisdiction for any court proceedings if necessary</ListItem>
            </List>
          </Section>

          <Section>
            <SectionTitle>Website Use and Restrictions</SectionTitle>
            <Paragraph>
              Regarding use of our website antonyhaines.com:
            </Paragraph>
            <List>
              <ListItem>Content is provided for informational purposes only</ListItem>
              <ListItem>Unauthorized reproduction or distribution is prohibited</ListItem>
              <ListItem>Case studies represent actual results with confidentiality protections</ListItem>
              <ListItem>Past performance does not guarantee future results</ListItem>
              <ListItem>Website availability is not guaranteed and may be interrupted</ListItem>
            </List>
          </Section>

          <Section>
            <SectionTitle>Changes to Terms</SectionTitle>
            <Paragraph>
              We reserve the right to modify these Terms at any time. Changes will be effective 
              immediately upon posting on our website. Continued use of our website or services 
              constitutes acceptance of modified Terms. We encourage regular review of these Terms.
            </Paragraph>
          </Section>

          <Section>
            <SectionTitle>Severability</SectionTitle>
            <Paragraph>
              If any provision of these Terms is found to be unenforceable or invalid, the remaining 
              provisions will continue in full force and effect. Invalid provisions will be modified 
              to the minimum extent necessary to make them enforceable while preserving the original intent.
            </Paragraph>
          </Section>

          <Section>
            <SectionTitle>Contact Information</SectionTitle>
            <ContactInfo>
              <ContactTitle>Questions About These Terms?</ContactTitle>
              <ContactText>
                If you have questions about these Terms of Service or need clarification about 
                our consulting services, please contact us:
              </ContactText>
              <ContactText style={{ marginTop: '8px' }}>
                <strong>Email:</strong> antony@antonyhaines.com<br />
                <strong>Website:</strong> antonyhaines.com<br />
                <strong>Business:</strong> Antony Haines Consulting - Fractional CTO Services<br />
                <strong>Specialization:</strong> US Mid-Market Manufacturing ($100M-$2B Revenue)
              </ContactText>
            </ContactInfo>
          </Section>

          <Section>
            <Disclaimer>
              <ContactText>
                <strong>Disclaimer:</strong> These Terms of Service provide general guidelines for 
                our consulting services. Specific engagements are governed by separate consulting 
                agreements that take precedence over these general terms. Professional consulting 
                advice should be considered within your specific business context and regulatory environment.
              </ContactText>
            </Disclaimer>
          </Section>
        </ModalBody>
      </ModalContent>
    </ModalOverlay>
  );
};

export default TermsOfService;